/**
 * Manager Dashboard Section (Epic 5, Story 5.1 — Rolgebaseerd dashboard, UX V7)
 *
 * Leidinggevende-sectie (aanvullend, capability-gated op `isTeamManagerOrHR`
 * — AD-4/AD-8, zie `AuthContext.tsx`): teller-tegel "N openstaande
 * verlofaanvragen" (Story 3.1) + team-aanwezigheidskalender-doorkijk deze
 * week (Story 4.1/4.2) + teller-tegel "N planningsconflicten" (Epic C-4,
 * Story C-4.3). Hergebruikt bestaande hooks/endpoints — geen tweede
 * implementatie van de goedkeuringslijst-/kalender-/conflict-logica (AD-9).
 *
 * Code-review fix: de aanwezig-vandaag-telling hergebruikt nu Epic 4's
 * `tellForDate` (`features/aanwezigheid/utils/telling.ts`) in plaats van een
 * eigen inline dagdeel-check — die inline versie telde `verlof_in_afwachting`
 * en dagdeel-precisie (halve dagen) fout, en sprak de eigen AD-9-docstring
 * hierboven tegen (twee implementaties van dezelfde telling).
 *
 * Story C-4.3 (AC 4): de conflicten-tegel volgt EXACT het bestaande
 * "teller-tegel 'N openstaande aanvragen'"-precedent hierboven — geen nieuw
 * dashboard-compositiepatroon, gewoon een derde tegel in dezelfde grid.
 * Venster: lopende + volgende week (geratificeerd, geen open punt). Een
 * gewone `useConflicts`-`useQuery` — geen SSE/polling-toevoeging (AD-12).
 */

import { Link } from 'react-router-dom';
import { useApprovalQueue } from '../verlof';
import { useAanwezigheidskalender } from '../aanwezigheid/hooks/useAanwezigheidskalender';
import { tellForDate } from '../aanwezigheid/utils/telling';
import { useConflicts } from '../planning/hooks/useConflicts';
import { getMondayOfWeek, getWeekDates, getWorkweekDates, addWeeks, toDateString } from '../werkrooster/utils/dateUtils';

/**
 * Ophaalvenster voor de conflicten-teller. Bewust de VOLLEDIGE kalenderweek
 * (`getWeekDates`), niet de werkweek: dit is geen weergave maar een teller,
 * en een venster versmallen kan een conflict buiten beeld laten vallen.
 * Te veel ophalen is hier de veilige kant.
 */
function useLopendeEnVolgendeWeekRange(): { van: string; tot: string } {
  const monday = getMondayOfWeek(new Date());
  const dates = getWeekDates(monday).map(toDateString);
  const nextMonday = addWeeks(monday, 1);
  const nextDates = getWeekDates(nextMonday).map(toDateString);
  return { van: dates[0] ?? toDateString(monday), tot: nextDates[nextDates.length - 1] ?? toDateString(nextMonday) };
}

function useCurrentWeekRange(): { van: string; tot: string } {
  const monday = getMondayOfWeek(new Date());
  const dates = getWorkweekDates(monday).map(toDateString);
  return { van: dates[0] ?? toDateString(monday), tot: dates[dates.length - 1] ?? toDateString(monday) };
}

export function ManagerDashboardSection() {
  const { data: queueData, isLoading: isLoadingQueue, isError: isQueueError } = useApprovalQueue();
  const { van, tot } = useCurrentWeekRange();
  const { data: calendarData, isLoading: isLoadingCalendar, isError: isCalendarError } = useAanwezigheidskalender(van, tot);

  const { van: conflictsVan, tot: conflictsTot } = useLopendeEnVolgendeWeekRange();
  const { data: conflictsData, isLoading: isLoadingConflicts, isError: isConflictsError } = useConflicts(conflictsVan, conflictsTot);
  const conflictsCount = (conflictsData?.conflicts.length ?? 0) + (conflictsData?.qualificationWarnings.length ?? 0);

  const openstaandCount = (queueData?.data ?? []).filter((item) => item.requestStatus === 'in_afwachting').length;

  // Defensieve guards tegen malformed/partiële API-respons — de widget mag
  // nooit crashen op een onverwachte vorm, ook al is dat vandaag niet de
  // contractvorm van `AttendanceCalendarResponse`. `tellForDate` zelf gaat
  // ervan uit dat `employees` een array is (Epic 4's contract) — hier
  // expliciet gecontroleerd vóór de call, zodat een onvolledige respons
  // nooit een crash geeft, alleen een lege telling.
  const calendarResponse = calendarData?.data;
  const hasValidEmployeeList = Array.isArray(calendarResponse?.employees);
  const totaalTeamleden = hasValidEmployeeList ? calendarResponse!.employees.length : 0;
  const today = toDateString(new Date());
  let telling = { kantoor: 0, thuis: 0, afwezig: 0 };
  if (hasValidEmployeeList) {
    try {
      telling = tellForDate(calendarResponse!, today);
    } catch (error) {
      // Laatste verdedigingslinie: een onverwacht gevormd dagdeel (bv.
      // `morning`/`afternoon` ontbreekt op een individuele dag-rij) mag deze
      // widget nooit laten crashen — degradeer naar een lege telling.
      console.error('ManagerDashboardSection: tellForDate failed on malformed calendar data', error);
    }
  }
  const aanwezigVandaag = telling.kantoor + telling.thuis;

  return (
    <div data-testid="manager-dashboard-section" className="grid grid-cols-1 md:grid-cols-3 gap-6">
      <Link
        to="/goedkeuringen"
        data-testid="openstaande-aanvragen-teller"
        className="block bg-white p-6 rounded-lg border border-[#E2E8F0] shadow-sm hover:shadow-md transition-shadow"
      >
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-500">Openstaande verlofaanvragen</p>
            {isQueueError ? (
              <p data-testid="openstaande-aanvragen-error" className="text-sm text-[#D64545] mt-1">
                Kon niet laden
              </p>
            ) : (
              <p className="text-3xl font-bold text-[#2F5A7A] mt-1">
                {isLoadingQueue ? '—' : openstaandCount}
              </p>
            )}
          </div>
          <span className="inline-block px-3 py-1 rounded-full bg-[#54949E]/10 text-[#54949E] text-xs font-medium">
            Goedkeuringen →
          </span>
        </div>
      </Link>

      <Link
        to="/aanwezigheid"
        data-testid="team-kalender-doorkijk"
        className="block bg-white p-6 rounded-lg border border-[#E2E8F0] shadow-sm hover:shadow-md transition-shadow"
      >
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-500">Team-aanwezigheid vandaag</p>
            {isCalendarError ? (
              <p data-testid="team-kalender-error" className="text-sm text-[#D64545] mt-1">
                Kon niet laden
              </p>
            ) : (
              <p className="text-3xl font-bold text-[#2F5A7A] mt-1">
                {isLoadingCalendar ? '—' : `${aanwezigVandaag} / ${totaalTeamleden}`}
              </p>
            )}
          </div>
          <span className="inline-block px-3 py-1 rounded-full bg-[#54949E]/10 text-[#54949E] text-xs font-medium">
            Aanwezigheidskalender →
          </span>
        </div>
      </Link>

      <Link
        to="/planning/planbord"
        data-testid="planningsconflicten-teller"
        className="block bg-white p-6 rounded-lg border border-[#E2E8F0] shadow-sm hover:shadow-md transition-shadow"
      >
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-500">Planningsconflicten</p>
            {isConflictsError ? (
              <p data-testid="planningsconflicten-error" className="text-sm text-[#D64545] mt-1">
                Kon niet laden
              </p>
            ) : isLoadingConflicts ? (
              <p className="text-3xl font-bold text-[#2F5A7A] mt-1">—</p>
            ) : conflictsCount === 0 ? (
              <p className="flex items-center gap-1.5 text-sm font-medium mt-1" style={{ color: '#3F6212' }}>
                <span aria-hidden="true">&#10003;</span>
                Geen conflicten — alles werkbaar.
              </p>
            ) : (
              <p className="text-3xl font-bold" style={{ color: '#D64545' }}>
                {conflictsCount}
              </p>
            )}
          </div>
          {!isLoadingConflicts && !isConflictsError && conflictsCount > 0 && (
            <span
              className="inline-block px-3 py-1 rounded-full text-xs font-medium"
              style={{ backgroundColor: '#D6454514', color: '#D64545' }}
            >
              Bekijk conflicten →
            </span>
          )}
        </div>
      </Link>
    </div>
  );
}
